import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js";
import * as Mailcheck from "mailcheck";
import isEmail from "validator/lib/isEmail";
const validateInputValue = (handleValidationChange, value, type, required, customErrorMessages) => {
  const errorMessages = {
    fieldRequired: customErrorMessages?.fieldRequired ?? "This field is required",
    invalidEmail: customErrorMessages?.invalidEmail ?? "Please enter a valid email address",
    invalidPhoneNumber: customErrorMessages?.invalidPhoneNumber ?? "Please enter a valid phone number"
  };
  if (value === "" && required)
    handleValidationChange(errorMessages.fieldRequired);
  else if (type === "emailAddress" && value.length > 0) {
    const isEmailValid = validateEmail(value) ? errorMessages.invalidEmail : void 0;
    handleValidationChange(isEmailValid);
  } else if (type === "telephoneNumber" && value.length > 0) {
    const isPhoneValid = validatePhoneNumber(value) ? errorMessages.invalidPhoneNumber : void 0;
    handleValidationChange(isPhoneValid);
  } else
    handleValidationChange(void 0);
}, BLACKLISTED_TLDS = ["como", "comp", "con", "met", "ner", "vom"], validateEmail = (value) => {
  const tld = value.split(".").pop(), blackListed = tld ? BLACKLISTED_TLDS.includes(tld) : !1;
  return !isEmail(value) || blackListed;
}, validatePhoneNumber = (value) => !parsePhoneNumberFromString(value, "US")?.isValid(), formatInputValue = (value, type, getValue) => {
  let unformattedValue = value, formattedValue = value;
  return type === "percentage" && (formattedValue = clampPercentage(value), unformattedValue = formattedValue), (type === "number" || type === "dollars") && (unformattedValue = unformatNumber(value), formattedValue = formatNumber(value)), type === "telephoneNumber" && (unformattedValue = unFormatPhoneNumber(value), formattedValue = formatPhoneNumber(value)), getValue && getValue({ formattedValue, unformattedValue }), formattedValue;
}, clampPercentage = (text) => {
  const digits = text.replace(/\D/g, "");
  return digits.startsWith("0") ? "0" : digits.startsWith("100") ? "100" : digits.substring(0, 2);
}, unformatNumber = (value) => value.replace(/\D/g, ""), MAX_NUMBER_LENGTH = 15, MAX_NUMBER_LENGTH_WITH_COMMAS = 19, formatNumber = (dollars) => {
  const digits = dollars.replace(/\D/g, "").slice(0, MAX_NUMBER_LENGTH);
  if (digits === "")
    return digits;
  const value = parseInt(digits, 10);
  return new Intl.NumberFormat("en-US").format(value);
}, unFormatPhoneNumber = (value) => value.replace(/\D/g, ""), formatPhoneNumber = (value) => (value[0] === "(" && value.length <= 4 || (value = new AsYouType("US").input(value)), value), mailcheckDefaultDomains = [
  // Keep in sync with app/services/BriteVerifyService
  "yahoo.com",
  "google.com",
  "hotmail.com",
  "gmail.com",
  "me.com",
  "aol.com",
  "mac.com",
  "live.com",
  "comcast.net",
  "googlemail.com",
  "msn.com",
  "facebook.com",
  "verizon.net",
  "sbcglobal.net",
  "att.net",
  "mail.com",
  "outlook.com",
  "icloud.com",
  "cox.net"
], mailcheckDefaultTopLevelDomains = ["com", "net", "org", "info", "edu"], suggestEmail = (email) => new Promise((resolve) => {
  Mailcheck.run({
    domains: mailcheckDefaultDomains,
    email,
    empty: () => resolve(""),
    suggested: (suggestion) => {
      suggestion && suggestion.domain.includes(".") ? resolve(JSON.stringify(suggestion.full).replace(/"/g, "")) : resolve("");
    },
    topLevelDomains: mailcheckDefaultTopLevelDomains
  });
}), getSuggestion = async (value, type) => {
  if (type === "emailAddress")
    return await suggestEmail(value);
}, getFormatProps = ({
  format,
  value
}) => {
  switch (format) {
    case "none":
      return {
        textContentType: "none",
        keyboardType: "default"
      };
    case "emailAddress":
      return {
        textContentType: "emailAddress",
        keyboardType: "email-address"
      };
    case "telephoneNumber":
      return {
        textContentType: "telephoneNumber",
        keyboardType: "phone-pad",
        maxLength: 16
      };
    case "password":
      return {
        textContentType: "password",
        keyboardType: "default"
      };
    case "percentage":
      return {
        inputMode: "numeric",
        suffix: value && value.length > 0 ? "%" : void 0,
        /**
         * Note: There's currently an issue with React Native where controlled
         * inputs can cause flickering if you type really quickly because the
         * native text value is set before the JS state. Setting a precise
         * maxLength seems to help.  If we encounter issues in the future, we
         * might consider setting the native text value ourselves.
         * See: https://github.com/facebook/react-native/issues/23578#issuecomment-1903470830
         */
        maxLength: value && value[0] === "1" && value[1] === "0" ? 3 : value && value[0] === "0" ? 1 : 2
      };
    case "number":
      return {
        inputMode: "numeric",
        maxLength: MAX_NUMBER_LENGTH_WITH_COMMAS
      };
    case "dollars":
      return {
        inputMode: "numeric",
        prefix: "$",
        maxLength: MAX_NUMBER_LENGTH_WITH_COMMAS
      };
  }
}, useDebounce = (callback, delay) => {
  const callbackRef = useRef(callback), timerRef = useRef();
  return useLayoutEffect(() => {
    callbackRef.current = callback;
  }), useEffect(() => () => {
    timerRef.current !== void 0 && clearTimeout(timerRef.current);
  }, []), useMemo(
    () => (...args) => {
      timerRef.current !== void 0 && clearTimeout(timerRef.current), timerRef.current = setTimeout(() => {
        timerRef.current = void 0, callbackRef.current(...args);
      }, delay);
    },
    [delay]
  );
};
export {
  formatInputValue,
  getFormatProps,
  getSuggestion,
  mailcheckDefaultDomains,
  useDebounce,
  validateInputValue
};
